import React, { Component } from 'react';
import { Row, Col, Form } from "react-bootstrap";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import { withRouter } from "react-router-dom";
import * as Cookies from "es-cookie";
import CardLessDataTable from '../../datatables/CardLessDataTableWithSearch';
import { attachModuleGuard, AccessDeniedModal } from "../../../../utils/moduleGuard";

// /Fee-Structure/* → "Fee_Structure". Match on module_key only.
// This tab owns the ONLY server-side delete in the whole Fee Structure module.
const FEE_STRUCTURE_MODULE = ["Fee_Structure"];

const ADD_FEE_TYPE = gql`
    mutation($params: AddFeeTypes) {
        addFeeTypes(params: $params)
    }
`;

const GET_FEE_TYPES = gql`
    query($institute_id: Int) {
        getFeeTypes(institute_id: $institute_id) {
            id
            fee_type_name
            is_report
        }
    }
`;

const DELETE_FEE_TYPE = gql`
    mutation($id: Int) {
        deleteFeeType(id: $id)
    }
`;

class FeeTypeSection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            feeType: "",
            feeTypeError: "",
            isActive: true,
            submitError: "",
            successMessage: "",
            loadingSpinner: false,
            feeTypeList: [],
            loader: false,
            editingFeeTypeId: null,
            editingFeeTypeName: "",
            editingIsReport: true,
            showEditModal: false,
            showAddModal: false,
            accessDeniedShow: false,
        };

        this.guard = attachModuleGuard(this, FEE_STRUCTURE_MODULE);
    }

    // Modal openers — gated before the form appears; the submits re-check at the write.
    openAddModal = () => {
        if (!this.guard.requireEdit()) return;

        this.setState({ showAddModal: true, feeType: "", feeTypeError: "", isActive: true, submitError: "", successMessage: "" });
    };

    closeAddModal = () => {
        this.setState({ showAddModal: false, feeTypeError: "", submitError: "", successMessage: "" });
    };

    handleEditClick = (feeType) => {
        if (!this.guard.requireEdit()) return;

        this.setState({
            editingFeeTypeId: feeType.id,
            editingFeeTypeName: feeType.fee_type_name,
            editingIsReport: feeType.is_report === 1,
            showEditModal: true,
            submitError: "",
            successMessage: "",
        });
    };

    handleEditInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "editingFeeTypeName") {
            this.setState({ editingFeeTypeName: value });
        } else if (name === "editingIsReport") {
            this.setState({ editingIsReport: checked });
        }
    };

    // Last gate before the ADD_FEE_TYPE upsert leaves the client.
    handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!this.guard.requireEdit()) return;

        const { editingFeeTypeId, editingFeeTypeName, editingIsReport } = this.state;
        if (!editingFeeTypeName.trim()) {
            this.setState({ submitError: "Please enter a fee type name" });
            return;
        }
        this.setState({ loadingSpinner: true, submitError: "", successMessage: "" });
        try {
            await this.props.client.mutate({
                mutation: ADD_FEE_TYPE,
                variables: {
                    params: {
                        id: editingFeeTypeId,
                        fee_type_name: editingFeeTypeName.trim(),
                        is_report: editingIsReport ? 1 : 0,
                        institute_id: parseInt(Cookies.get("institutionid")),
                    },
                },
            });
            this.setState({
                loadingSpinner: false,
                successMessage: "Fee type updated successfully",
                showEditModal: false,
            });
            setTimeout(() => this.resetForm(), 1500);
        } catch (error) {
            this.setState({
                loadingSpinner: false,
                submitError:
                    error.graphQLErrors?.map((x) => x.message).join(", ") ||
                    "Update failed. Please try again.",
            });
        }
    };

    handleEditModalClose = () => {
        this.setState({ showEditModal: false, submitError: "", successMessage: "" });
    };

    // The only server-side delete in the Fee Structure module. Checked BEFORE the confirm
    // prompt: a role without is_delete should be told they lack access, not asked to
    // confirm a delete that can't happen.
    handleDeleteClick = async (feeType) => {
        if (!this.guard.requireDelete()) return;

        if (!window.confirm(`Delete fee type "${feeType.fee_type_name}"?`)) return;
        try {
            await this.props.client.mutate({
                mutation: DELETE_FEE_TYPE,
                variables: { id: feeType.id },
            });
            this.getFeeTypes();
        } catch (error) {
            alert(error.graphQLErrors?.map((x) => x.message).join(", ") || "Delete failed. Please try again.");
        }
    };

    componentDidMount() {
        this.getFeeTypes();
    }

    getFeeTypes = async () => {
        this.setState({ loader: true });
        try {
            const result = await this.props.client.query({
                query: GET_FEE_TYPES,
                variables: { institute_id: parseInt(Cookies.get("institutionid")) },
                fetchPolicy: "network-only",
            });
            this.setState({
                feeTypeList: result.data.getFeeTypes || [],
                loader: false,
            });
        } catch {
            this.setState({ loader: false });
        }
    };

    handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "feeType") {
            this.setState({ feeType: value, feeTypeError: "", submitError: "" });
        }
    };

    // Last gate before the create mutation leaves the client.
    handleFormSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!this.guard.requireEdit()) return;


        const { feeType, isActive } = this.state;
        let hasError = false;

        if (!feeType || !feeType.trim()) {
            this.setState({ feeTypeError: "Please enter a fee type" });
            hasError = true;
        }

        if (hasError) return;

        this.setState({ loadingSpinner: true, submitError: "", successMessage: "" });

        try {
            await this.props.client.mutate({
                mutation: ADD_FEE_TYPE,
                variables: {
                    params: {
                        id:null,
                        fee_type_name: feeType.trim(),
                        is_report: isActive ? 1 : 0,
                        institute_id: parseInt(Cookies.get("institutionid")),
                    },
                },
            });

            this.setState({
                loadingSpinner: false,
                successMessage: "Fee type added successfully",
            });

            setTimeout(() => this.resetForm(), 1500);
        } catch (error) {
            this.setState({
                loadingSpinner: false,
                submitError:
                    error.graphQLErrors?.map((x) => x.message).join(", ") ||
                    "Submission failed. Please try again.",
            });
        }
    };

    resetForm = () => {
        this.setState({
            feeType: "",
            feeTypeError: "",
            isActive: true,
            submitError: "",
            successMessage: "",
            loadingSpinner: false,
            showAddModal: false,
        });
        this.getFeeTypes();
    };

    Columns() {
        return [
            {
                dataField: "sno",
                text: "#",
                formatter: (cell, row, rowIndex) => rowIndex + 1,
            },
            {
                dataField: "fee_type_name",
                text: "Fee Type",
                sort: true,
            },
            {
                dataField: "is_report",
                text: "is_report",
                formatter: (cell) => (
                    <span
                        style={{
                            padding: "3px 12px",
                            borderRadius: "12px",
                            fontSize: "13px",
                            fontWeight: "500",
                            background: cell == 1 ? "#e6f4ea" : "#fce8e6",
                            color: cell == 1 ? "#1e7e34" : "#c0392b",
                        }}
                    >
                        {cell == 1 ? "Active" : "Inactive"}
                    </span>
                ),
            },
            {
                dataField: "edit",
                text: "Actions",
                formatter: (cell, row) => (
                    <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        <button
                            className="btn btn-sm btn-primary"
                            style={{ borderRadius: 6, fontSize: 13, padding: "3px 12px" }}
                            onClick={() => this.handleEditClick(row)}
                        >
                            Edit
                        </button>
                        <button
                            className="btn btn-sm btn-danger"
                            style={{ borderRadius: 6, fontSize: 13, padding: "3px 10px" }}
                            title="Delete"
                            onClick={() => this.handleDeleteClick(row)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: "middle" }}>
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                            </svg>
                        </button>
                    </div>
                ),
            },
        ];
    }

    render() {
        const {
            feeType, feeTypeError,
            isActive,
            submitError, successMessage, loadingSpinner,
            feeTypeList, loader,
        } = this.state;

        const S = {
            page: { padding: "8px 0" },
            card: { background: "#fff", borderRadius: 20, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "none", marginBottom: 24, overflow: "hidden" },
            cardHeader: {
                background: "#ffffff",
                padding: "20px 24px",
                display: "flex",
                alignItems: "center",
                gap: 14,
                borderBottom: "1px solid #f1f5f9",
            },
            iconWrapperCreate: {
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "#e0e7ff",
                border: "1px solid #c7d2fe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#4f46e5",
                fontSize: 16,
                flexShrink: 0,
            },
            iconWrapperList: {
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "#f5f3ff",
                border: "1px solid #ddd6fe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#7c3aed",
                fontSize: 16,
                flexShrink: 0,
            },
            cardTitleText: {
                display: "flex",
                flexDirection: "column",
                textAlign: "left",
            },
            cardTitle: {
                color: "#0f172a",
                fontWeight: 700,
                fontSize: 16,
                margin: 0,
                letterSpacing: "-0.01em",
            },
            cardSubtitle: {
                color: "#64748b",
                fontSize: 12,
                fontWeight: 500,
                marginTop: 2,
            },
            cardBody: { padding: "22px 24px" },
            label: { fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px", display: "block" },
            input: { borderRadius: 10, border: "1.5px solid #e5e7eb", padding: "9px 14px", fontSize: 14, color: "#1f2937", background: "#f9fafb", width: "100%" },
            inputError: { borderRadius: 10, border: "1.5px solid #ef4444", padding: "9px 14px", fontSize: 14, color: "#1f2937", background: "#f9fafb", width: "100%" },
            addBtn: { background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", border: "none", borderRadius: 10, padding: "9px 24px", fontWeight: 700, fontSize: 14, color: "#fff", boxShadow: "0 4px 12px rgba(16,185,129,0.3)", cursor: "pointer" },
            countChip: {
                background: "#f5f3ff",
                color: "#7c3aed",
                borderRadius: 20,
                padding: "4px 12px",
                fontWeight: 600,
                fontSize: 12,
                border: "1px solid #ddd6fe",
                marginLeft: "auto",
            },
        };

        return (
            <div style={S.page}>
                {/* ── Add Fee Type Modal ── */}
                {this.state.showAddModal && (
                    <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.3)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <div style={{ background: "#fff", borderRadius: 16, padding: 32, minWidth: 380, boxShadow: "0 2px 16px rgba(0,0,0,0.15)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
                                <div style={S.iconWrapperCreate}><i className="fal fa-tag" /></div>
                                <h5 style={{ margin: 0 }}>Add Fee Type</h5>
                                <button type="button" onClick={this.closeAddModal} style={{ marginLeft: "auto", background: "none", border: "none", fontSize: 22, lineHeight: 1, color: "#94a3b8", cursor: "pointer" }}>&times;</button>
                            </div>
                            <Form onSubmit={this.handleFormSubmit}>
                                <div style={{ marginBottom: 16 }}>
                                    <label style={S.label}>Fee Type <span style={{ color: "#ef4444" }}>*</span></label>
                                    <input
                                        type="text"
                                        name="feeType"
                                        placeholder="Enter Fee Type"
                                        value={feeType}
                                        onChange={this.handleInputChange}
                                        style={feeTypeError ? S.inputError : S.input}
                                    />
                                    {feeTypeError && (
                                        <div style={{ color: "#ef4444", fontSize: 12, marginTop: 4 }}>{feeTypeError}</div>
                                    )}
                                </div>
                                <div style={{ marginBottom: 16 }}>
                                    <Form.Check
                                        type="checkbox"
                                        id="add-active-checkbox"
                                        label={<span style={{ cursor: 'pointer' }}>Active</span>}
                                        checked={isActive}
                                        onChange={(e) => this.setState({ isActive: e.target.checked })}
                                    />
                                </div>
                                <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                                    <button type="button" className="btn btn-secondary" style={{ borderRadius: 8, padding: "6px 22px" }} onClick={this.closeAddModal}>
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        style={{ ...S.addBtn, opacity: loadingSpinner ? 0.7 : 1 }}
                                        disabled={loadingSpinner}
                                    >
                                        {loadingSpinner ? (
                                            <><span className="spinner-border spinner-border-sm mr-2" role="is_report" />Saving...</>
                                        ) : "Submit"}
                                    </button>
                                </div>
                                {successMessage && (
                                    <div style={{ color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "8px 14px", fontSize: 14, marginTop: 16, display: "inline-block" }}>
                                        {successMessage}
                                    </div>
                                )}
                                {submitError && (
                                    <div style={{ color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 14px", fontSize: 14, marginTop: 16, display: "inline-block" }}>
                                        {submitError}
                                    </div>
                                )}
                            </Form>
                        </div>
                    </div>
                )}

                {/* ── Edit Fee Type Modal ── */}
                {this.state.showEditModal && (
                    <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.3)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <div style={{ background: "#fff", borderRadius: 16, padding: 32, minWidth: 340, boxShadow: "0 2px 16px rgba(0,0,0,0.15)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
                                <div style={S.iconWrapperCreate}><i className="fal fa-tag" /></div>
                                <h5 style={{ margin: 0 }}>Edit Fee Type</h5>
                                <button type="button" onClick={this.handleEditModalClose} style={{ marginLeft: "auto", background: "none", border: "none", fontSize: 22, lineHeight: 1, color: "#94a3b8", cursor: "pointer" }}>&times;</button>
                            </div>
                            <Form onSubmit={this.handleEditSubmit}>
                                <div style={{ marginBottom: 16 }}>
                                    <label style={S.label}>Fee Type Name <span style={{ color: "#ef4444" }}>*</span></label>
                                    <input
                                        type="text"
                                        name="editingFeeTypeName"
                                        value={this.state.editingFeeTypeName}
                                        onChange={this.handleEditInputChange}
                                        style={S.input}
                                    />
                                </div>
                                <div style={{ marginBottom: 16 }}>
                                    <Form.Check
                                        type="checkbox"
                                        id="edit-active-checkbox"
                                        label={<span style={{ cursor: 'pointer' }}>Active</span>}
                                        name="editingIsReport"
                                        checked={this.state.editingIsReport}
                                        onChange={this.handleEditInputChange}
                                    />
                                </div>
                                <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                                    <button type="button" className="btn btn-secondary" style={{ borderRadius: 8, padding: "6px 22px" }} onClick={this.handleEditModalClose}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-success" style={{ borderRadius: 8, padding: "6px 22px" }} disabled={loadingSpinner}>
                                        {loadingSpinner ? "Saving..." : "Save"}
                                    </button>
                                </div>
                                {submitError && (
                                    <div style={{ color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 14px", fontSize: 14, marginTop: 16, display: "inline-block" }}>
                                        {submitError}
                                    </div>
                                )}
                                {successMessage && (
                                    <div style={{ color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "8px 14px", fontSize: 14, marginTop: 16, display: "inline-block" }}>
                                        {successMessage}
                                    </div>
                                )}
                            </Form>
                        </div>
                    </div>
                )}

                {/* ── Fee Type List Card ── */}
                {loader ? (
                    <div style={S.card}>
                        <style>{`
                            @keyframes shimmer {
                                0% { background-position: -600px 0; }
                                100% { background-position: 600px 0; }
                            }
                            .shimmer-line {
                                background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                                background-size: 600px 100%;
                                animation: shimmer 1.4s infinite linear;
                                border-radius: 6px;
                            }
                        `}</style>
                        <div style={{ background: "#ffffff", padding: "20px 24px", display: "flex", alignItems: "center", gap: 14, borderBottom: "1px solid #f1f5f9" }}>
                            <div className="shimmer-line" style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0 }} />
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <div className="shimmer-line" style={{ width: 100, height: 16 }} />
                                <div className="shimmer-line" style={{ width: 220, height: 11 }} />
                            </div>
                        </div>
                        <div style={{ padding: "16px 24px" }}>
                            {/* shimmer header row */}
                            <div style={{ display: "flex", gap: 16, marginBottom: 12, padding: "8px 0", borderBottom: "2px solid #f3f4f6" }}>
                                {[60, 180, 100, 70].map((w, i) => (
                                    <div key={i} className="shimmer-line" style={{ height: 14, width: w, borderRadius: 4 }} />
                                ))}
                            </div>
                            {/* shimmer rows */}
                            {[1, 2, 3, 4, 5].map((row) => (
                                <div key={row} style={{ display: "flex", gap: 16, padding: "12px 0", borderBottom: "1px solid #f3f4f6", alignItems: "center" }}>
                                    <div className="shimmer-line" style={{ height: 12, width: 60 }} />
                                    <div className="shimmer-line" style={{ height: 12, width: 180 }} />
                                    <div className="shimmer-line" style={{ height: 22, width: 100, borderRadius: 12 }} />
                                    <div className="shimmer-line" style={{ height: 26, width: 70, borderRadius: 6 }} />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div style={{ ...S.card, overflow: "visible" }}>
                        <div style={S.cardHeader}>
                            <div style={S.iconWrapperList}>
                                <i className="fal fa-list-alt" />
                            </div>
                            <div style={S.cardTitleText}>
                                <h5 style={S.cardTitle}>Fee Type List</h5>
                                <span style={S.cardSubtitle}>View and manage configured fee categories</span>
                            </div>
                            <span style={S.countChip}>{feeTypeList.length} types</span>
                            <button style={{ ...S.addBtn, marginLeft: 12 }} onClick={this.openAddModal}>
                                <i className="fal fa-plus mr-2" />Add Fee Type
                            </button>
                        </div>
                        <div style={{ padding: "16px 24px" }}>
                            <CardLessDataTable
                                parentData={feeTypeList}
                                particlecolumns={this.Columns()}
                                search={true}
                                name="fee_type_list"
                            />
                        </div>
                    </div>
                )}

                {/* Shown instead of creating / editing / deleting a fee type when the
                    grant is missing. One modal serves every intercepted action. */}
                <AccessDeniedModal
                    show={this.state.accessDeniedShow}
                    onHide={this.guard.hideAccessDenied}
                />
            </div>
        );
    }
}

export default withRouter(withApollo(FeeTypeSection));
